import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    XMarkIcon,
    MinusIcon,
    PlusIcon,
    PhotoIcon,
    ShieldCheckIcon,
    TruckIcon
} from '@heroicons/react/24/outline';
import {
    StarIcon as StarSolidIcon,
    CheckCircleIcon
} from '@heroicons/react/24/solid';
import { useAppContext } from '../context/AppContext';
import { toast } from 'react-hot-toast';

const ProductDetailModal = ({ product, isOpen, onClose }) => {
    const { addToCart } = useAppContext();
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    // Reset selectedSize when product changes
    useEffect(() => {
        setSelectedSize('');
    }, [product]);

    if (!isOpen || !product) return null;

    const handleAddToCart = async () => {
        if (product.sizes && product.sizes.length > 0 && !selectedSize) {
            toast.error('Please select a size before adding to cart.');
            return;
        }
        setIsLoading(true);
        await addToCart(product.id, quantity, selectedSize);
        setIsLoading(false);
        onClose();
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white w-screen h-screen max-w-none max-h-none overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button at top right of modal screen */}
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-8 bg-white/90 backdrop-blur-sm p-2 hover:bg-white transition-colors z-50"
                        style={{ position: 'fixed' }}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    {/* Responsive layout: mobile first */}
                    <div className="block lg:hidden p-4">
                        {/* Product Images */}
                        <div className="mb-6">
                            <div className="grid grid-cols-2 gap-4">
                                {product.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image}
                                        alt={product.title}
                                        className="w-full"
                                    />
                                ))}
                            </div>
                        </div>
                        {/* Title and Description */}
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">{product.title}</h2>
                            <div
                                className="text-gray-600 text-sm leading-relaxed"
                                dangerouslySetInnerHTML={{
                                    __html: product.description
                                        ? product.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
                                        : ''
                                }}
                            />
                        </div>
                        {/* Rest of Details */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                                <span className="text-2xl font-bold text-[#362222]">₹{product.price}</span>
                            </div>
                            <div className="flex items-center space-x-1 mt-2">
                                {[...Array(5)].map((_, i) => (
                                    <StarSolidIcon key={i} className="h-5 w-5 text-yellow-400" />
                                ))}
                                <span className="text-gray-500 ml-2">(4.8 rating)</span>
                            </div>
                            {/* Size Selection */}
                            {product.sizes && product.sizes.length > 0 && (
                                <div className="space-y-1">
                                    <h4 className="font-semibold text-lg mb-1">Size:</h4>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {product.sizes.map((size) => (
                                            <motion.button
                                                key={size}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => setSelectedSize(size)}
                                                className={`px-4 py-2 border text-sm rounded-md font-medium transition-all ${selectedSize === size
                                                    ? 'border-[#E31E24] bg-white text-[#E31E24] shadow-lg'
                                                    : 'border-gray-300 hover:border-[#E31E24] hover:shadow-md'
                                                    }`}
                                            >
                                                {size}
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Quantity Selection */}
                            <div className="space-y-3">
                                <h4 className="font-semibold text-lg mb-1">Quantity:</h4>
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                                    >
                                        <MinusIcon className="h-3 w-3" />
                                    </button>
                                    <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                                    >
                                        <PlusIcon className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                            {/* Action Buttons */}
                            <div className="space-y-4">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleAddToCart}
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-2 rounded-md text-base font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                            <span>Adding...</span>
                                        </div>
                                    ) : (
                                        'Add to Cart'
                                    )}
                                </motion.button>
                                <button className="w-full border-2 border-[#E31E24] text-[#E31E24] py-2 rounded-md text-base font-semibold hover:bg-[#E31E24] hover:text-white transition-all duration-300">
                                    Add to Wishlist
                                </button>
                            </div>
                            {/* Product Features */}
                            {product.is_customizable && (
                                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="bg-orange-100 p-2 rounded-lg">
                                            <PhotoIcon className="h-6 w-6 text-orange-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-orange-800 mb-2">Customization Available</h4>
                                            <p className="text-sm text-orange-700">
                                                This product can be personalized with your own design. Upload your image during checkout for a truly unique creation.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Trust badges */}
                            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                                <div className="text-center space-y-2">
                                    <ShieldCheckIcon className="h-8 w-8 text-green-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Quality Guaranteed</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <TruckIcon className="h-8 w-8 text-blue-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Fast Shipping</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <CheckCircleIcon className="h-8 w-8 text-purple-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Easy Returns</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Desktop layout: unchanged */}
                    <div className="hidden lg:grid grid-cols-[3fr_3fr_1.5fr] gap-8 p-8">
                        {/* Product Images */}
                        <div className="space-y-4">
                            <div className="relative overflow-hidden">
                                <div className="grid grid-cols-2 gap-4">
                                    {product.images.map((image, index) => (
                                        <img
                                            key={index}
                                            src={image}
                                            alt={product.title}
                                            className="w-full"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* Product Details */}
                        <div className="space-y-4">
                            <div>
                                <h2 className="text-4xl font-bold text-gray-800 mb-4">{product.title}</h2>
                                <div
                                    className="text-gray-600 text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: product.description
                                            ? product.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
                                            : ''
                                    }}
                                />
                            </div>
                            {/* Product Features */}
                            {product.is_customizable && (
                                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="bg-orange-100 p-2 rounded-lg">
                                            <PhotoIcon className="h-6 w-6 text-orange-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-orange-800 mb-2">Customization Available</h4>
                                            <p className="text-sm text-orange-700">
                                                This product can be personalized with your own design. Upload your image during checkout for a truly unique creation.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Trust badges */}
                            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                                <div className="text-center space-y-2">
                                    <ShieldCheckIcon className="h-8 w-8 text-green-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Quality Guaranteed</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <TruckIcon className="h-8 w-8 text-blue-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Fast Shipping</span>
                                </div>
                                <div className="text-center space-y-2">
                                    <CheckCircleIcon className="h-8 w-8 text-purple-500 mx-auto" />
                                    <span className="text-sm font-medium text-gray-700">Easy Returns</span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                                <span className="text-4xl font-bold text-[#362222]">₹{product.price}</span>
                            </div>
                            <div className="flex items-center space-x-1 mt-2">
                                {[...Array(5)].map((_, i) => (
                                    <StarSolidIcon key={i} className="h-5 w-5 text-yellow-400" />
                                ))}
                                <span className="text-gray-500 ml-2">(4.8 rating)</span>
                            </div>
                            {/* Size Selection */}
                            {product.sizes && product.sizes.length > 0 && (
                                <div className="space-y-1">
                                    <h4 className="font-semibold text-lg mb-1">Size:</h4>
                                    <div className="flex flex-wrap gap-2 mb-12">
                                        {product.sizes.map((size) => (
                                            <motion.button
                                                key={size}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => setSelectedSize(size)}
                                                className={`px-4 py-2 border text-sm rounded-md font-medium transition-all ${selectedSize === size
                                                    ? 'border-[#E31E24] bg-white text-[#E31E24] shadow-lg'
                                                    : 'border-gray-300 hover:border-[#E31E24] hover:shadow-md'
                                                    }`}
                                            >
                                                {size}
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Quantity Selection */}
                            <div className="space-y-3">
                                <h4 className="font-semibold text-lg mb-1">Quantity:</h4>
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                                    >
                                        <MinusIcon className="h-3 w-3" />
                                    </button>
                                    <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                                    >
                                        <PlusIcon className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                            {/* Action Buttons */}
                            <div className="space-y-4">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleAddToCart}
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-2 rounded-md text-base font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                            <span>Adding...</span>
                                        </div>
                                    ) : (
                                        'Add to Cart'
                                    )}
                                </motion.button>
                                <button className="w-full border-2 border-[#E31E24] text-[#E31E24] py-2 rounded-md text-base font-semibold hover:bg-[#E31E24] hover:text-white transition-all duration-300">
                                    Add to Wishlist
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ProductDetailModal;
